import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { setCsrfTokenForAxios } from './csrf-utils';

export type CsrfContextType = {
  csrfToken: string | null;
  refreshCsrfToken: () => Promise<void>;
  clearCsrfToken: () => void;
};

const CsrfContext = createContext<CsrfContextType>({
  csrfToken: null,
  refreshCsrfToken: async () => {},
  clearCsrfToken: () => {},
});

export const useCsrf = () => useContext(CsrfContext);

export type CsrfProviderProps = {
  children: ReactNode;
};

export const CsrfProvider: React.FC<CsrfProviderProps> = ({ children }) => {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  const fetchCsrfToken = async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000/';
      const url = new URL('/csrf-token', baseURL).href;
      const res = await fetch(url, {
        credentials: 'include',
      });
      const data = await res.json();
      setCsrfToken(data.csrfToken);
      setCsrfTokenForAxios(data.csrfToken);
    } catch (error) {
      console.error('Failed to fetch CSRF token', error);
    }
  };

  const clearCsrfToken = () => {
    setCsrfToken(null);
    setCsrfTokenForAxios(null);
  };

  useEffect(() => {
    fetchCsrfToken();
  }, []);

  return (
    <CsrfContext.Provider value={{ csrfToken, refreshCsrfToken: fetchCsrfToken, clearCsrfToken }}>
      {children}
    </CsrfContext.Provider>
  );
};
